/**
 * Admin Route Component
 *
 * Like ProtectedRoute, but additionally requires the signed-in user's email
 * to match the single dashboard admin account (config/adminAccess.ts).
 * Unauthenticated users are redirected to sign-in; authenticated non-admins
 * are redirected home with a toast rather than shown a bare 404/blank page.
 */

import { useUser } from '@clerk/clerk-react';
import { useLocation } from 'wouter';
import { ReactNode, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { isAdminEmail } from '@/config/adminAccess';

interface AdminRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export function AdminRoute({
  children,
  redirectTo = '/sign-in',
}: AdminRouteProps) {
  const { isLoaded, isSignedIn, user } = useUser();
  const [location, setLocation] = useLocation();
  const { toast } = useToast();

  const email = user?.primaryEmailAddress?.emailAddress ?? null;
  const isAdmin = isAdminEmail(email);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      const returnUrl = `${redirectTo}?returnTo=${encodeURIComponent(location)}`;
      setLocation(returnUrl);
      return;
    }

    if (!isAdmin) {
      toast({
        title: 'Admin Access Required',
        description: 'This page is only accessible to administrators.',
        variant: 'destructive',
      });
      setLocation('/');
    }
  }, [isLoaded, isSignedIn, isAdmin, location, redirectTo, setLocation, toast]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[var(--vault-black)] via-[var(--navy)] to-[var(--navy)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--brand)] mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn || !isAdmin) {
    return null;
  }

  return <>{children}</>;
}
